import React, {createContext, useContext, useEffect, useRef, useState} from "react";
import {isApp, isIOS, postRNMessage} from "common/utils";
import {
    MESSAGE_TYPE_DEVICE_UID_RESULT,
    MESSAGE_TYPE_FOREGROUND,
    MESSAGE_TYPE_PUSH_PERMISSION_RESULT,
    MESSAGE_TYPE_REQUEST_PUSH_PERMISSION_ANDROID_RESULT,
    MESSAGE_TYPE_REQUEST_PUSH_PERMISSION_RESULT,
    MESSAGE_TYPE_USER_AGENT_RESULT,
    redirectToLogin,
    REVERSE_MESSAGE_TYPE_GET_DEVICE_UID,
    REVERSE_MESSAGE_TYPE_GET_USER_AGENT,
    REVERSE_MESSAGE_TYPE_HAS_PUSH_PERMISSION,
    REVERSE_MESSAGE_TYPE_OPEN_SETTINGS,
    REVERSE_MESSAGE_TYPE_REQUEST_PUSH_PERMISSION,
    REVERSE_MESSAGE_TYPE_REQUEST_PUSH_PERMISSION_ANDROID,
} from "pages/_app";
import {v4} from "uuid";
import {Axios} from "api";
import {captureException, captureMessage} from "@sentry/nextjs";
import {toast} from "react-toastify";
import UserContext from "context/AuthContext";


const PushContext = createContext(undefined);

export default PushContext;

const LOCAL_STORAGE_KEY_PUSH_AGREEMENT_SET = 'push_agreement_set';

export const PUSH_AGREEMENT_INFORMATION = 'information';
export const PUSH_AGREEMENT_DAY_AD = 'day_ad';
export const PUSH_AGREEMENT_NIGHT_AD = 'night_ad';


export const PushContextProvider = ({children}) => {
    const callbackDictRef = useRef({});
    const user = useContext(UserContext).user;

    const [isDevicePushOn, setIsDevicePushOn] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [pushAgreementInfo, setPushAgreementInfo] = useState({
        isNightAdAgreed: false,
        isAdAgreed: false,
        isInformationAgreed: false,
    });
    const togglePushAgreementCbRef = useRef({cb: () => {}, validUntil: 0, agreementField: ''});
    const messageInitializedRef = useRef(false);

    const hasPushPermission = (callback) => {
        const callbackId = v4();
        callbackDictRef.current[callbackId] = callback;
        postRNMessage({
            type: REVERSE_MESSAGE_TYPE_HAS_PUSH_PERMISSION,
            key: callbackId,
        })
    }

    const requestPushPermission = (callback) => {
        const callbackId = v4();
        callbackDictRef.current[callbackId] = callback;
        postRNMessage({
            type: REVERSE_MESSAGE_TYPE_REQUEST_PUSH_PERMISSION,
            key: callbackId,
        })
    }

    const requestPushPermissionAndroid = (callback) => {
        const callbackId = v4();
        callbackDictRef.current[callbackId] = callback;
        postRNMessage({
            type: REVERSE_MESSAGE_TYPE_REQUEST_PUSH_PERMISSION_ANDROID,
            key: callbackId,
        })
    }

    const getDeviceUid = (callback) => {
        const callbackId = v4();
        callbackDictRef.current[callbackId] = callback;
        postRNMessage({
            type: REVERSE_MESSAGE_TYPE_GET_DEVICE_UID,
            key: callbackId,
        })
    }

    const getUserAgent = (callback) => {
        const callbackId = v4();
        callbackDictRef.current[callbackId] = callback;
        postRNMessage({
            type: REVERSE_MESSAGE_TYPE_GET_USER_AGENT,
            key: callbackId,
        })
    }

    const registerPushToken = (token) => {
        getDeviceUid(device_uid => {
            getUserAgent(async user_agent => {
                try {
                    const res = await Axios.post('v1/push/tokens', {
                        token: token,
                        device_uid: device_uid,
                        user_agent: user_agent,
                        platform: window.RNPlatform,
                    });
                    if (res.status >= 400) {
                        captureMessage(JSON.stringify(res.data));
                    }
                } catch (e) {
                    captureException(e);
                }
            });
        });
    }

    const resetTogglePushAgreementCb = () => {
        togglePushAgreementCbRef.current = {cb: () => {}, validUntil: 0, agreementField: ''};
    };

    useEffect(() => {
        resetTogglePushAgreementCb();
        if (user === undefined) return;
        hasPushPermission(enabled => {
            if (enabled > 0) {
                setIsDevicePushOn(true);
                requestPushPermission((enabled, token) => {
                    if (enabled > 0) {
                        setIsDevicePushOn(true);
                        registerPushToken(token);
                    }
                });
            } else {
                setIsDevicePushOn(false);
            }
        });
    }, [user])

    const sendPushAgreementRequest = async ({isDayAgree = null, isNightAgree = null, isInformationAgree = null}, onSuccess=null) => {
        typeof mixpanel !== 'undefined' && mixpanel.track(
            'PushAgreementRequested',
            {
                isDayAgree: isDayAgree,
                isNightAgree: isNightAgree,
                isInformationAgree: isInformationAgree,
            }
        )

        const argumentField = isDayAgree ? PUSH_AGREEMENT_DAY_AD : isNightAgree ? PUSH_AGREEMENT_NIGHT_AD : PUSH_AGREEMENT_INFORMATION;

        if (onSuccess) {
            togglePushAgreementCbRef.current = {cb: onSuccess, validUntil: Date.now() + 1000 * 60 * 5, agreementField: argumentField};
        }

        getDeviceUid(async device_uid => {
            try {
                if (isLoading) return;
                setIsLoading(true);

                const data = {};
                const bool = isDayAgree || isNightAgree || isInformationAgree;
                if (isDayAgree !== null) {
                    data.is_day_push_agreed = isDayAgree;
                }
                if (isNightAgree !== null) {
                    data.is_night_push_agreed = isNightAgree;
                }
                if (isInformationAgree !== null) {
                    data.is_informational_push_agreed = isInformationAgree;
                }

                const res = await Axios.put('v1/push/agreement', {
                    device_uid: device_uid,
                    ...data,
                });
                if (res.status < 400) {
                    if (typeof window === 'undefined') return;
                    if (bool) {
                        typeof mixpanel !== 'undefined' && mixpanel.track(
                            'PushAgreementAgreed',
                        );
                        hasPushPermission(enabled => {
                            if (enabled === -1) {
                                setIsDevicePushOn(false);

                                requestPushPermission((enabled, token) => {
                                    if (enabled > 0) {
                                        setIsDevicePushOn(true);
                                        registerPushToken(token);
                                    }
                                });
                            } else if (enabled === 0) {
                                setIsDevicePushOn(false);

                                // TODO: Android / IOS 분기
                                if (isIOS()) {
                                    const result = confirm('기기의 알림이 꺼져있어요. 알림 설정을 위해 휴대폰 설정창을 여시겠어요?');
                                    if (result) {
                                        postRNMessage({type: REVERSE_MESSAGE_TYPE_OPEN_SETTINGS});
                                    }
                                } else {
                                    requestPushPermissionAndroid((enabled, token) => {
                                        if (enabled) {
                                            setIsDevicePushOn(true);
                                            registerPushToken(token);
                                        }
                                    });
                                }
                            } else {
                                setIsDevicePushOn(true);
                            }
                        });
                    } else {
                        typeof mixpanel !== 'undefined' && mixpanel.track(
                            'PushAgreementDisagreed',
                        );
                    }
                    window.localStorage.setItem(LOCAL_STORAGE_KEY_PUSH_AGREEMENT_SET, 'true');
                    setPushAgreementInfo({
                        isNightAdAgreed: Boolean(res?.data?.is_night_ad_agreed),
                        isAdAgreed: Boolean(res?.data?.is_ad_agreed),
                        isInformationAgreed: Boolean(res?.data?.is_information_agreed),
                    });
                } else {
                    toast.info('푸시 수신 여부 처리에 오류가 발생했습니다.');
                    captureMessage(JSON.stringify(res.data));
                }
            } catch (e) {
                toast.info('푸시 수신 여부 처리에 오류가 발생했습니다.');
                captureException(e);
            } finally {
                setIsLoading(false);
            }
        });
    };

    useEffect(() => {
        if (togglePushAgreementCbRef.current) {
            if (!isDevicePushOn) {
                return;
            }
            if (togglePushAgreementCbRef.current.validUntil > Date.now()) {
                const cb = togglePushAgreementCbRef.current.cb;
                const agreementField = togglePushAgreementCbRef.current.agreementField;
                let isValid = false;
                if (agreementField === PUSH_AGREEMENT_INFORMATION) {
                    if (pushAgreementInfo.isInformationAgreed) {
                        isValid = true;
                    }
                } else if (agreementField === PUSH_AGREEMENT_DAY_AD) {
                    if (pushAgreementInfo.isAdAgreed) {
                        isValid = true;
                    }
                } else if (agreementField === PUSH_AGREEMENT_NIGHT_AD) {
                    if (pushAgreementInfo.isNightAdAgreed) {
                        isValid = true;
                    }
                }
                if (!isValid) {
                    return;
                }
                cb();
                resetTogglePushAgreementCb();
            }
        }
    }, [isDevicePushOn, pushAgreementInfo]);

    const onRNMessage = (messageEvent) => {
        let message = null;
        try {
            message = JSON.parse(messageEvent.data);
        } catch (e) {
            return;
        }

        if (message.type === MESSAGE_TYPE_PUSH_PERMISSION_RESULT) {
            const data = message.data;
            const callback = callbackDictRef.current[data.key] || function () {};
            const result = data.enabled;
            callback(result);
            if (callbackDictRef.current[data.key]) {
                delete callbackDictRef.current[data.key];
            }
        } else if (message.type === MESSAGE_TYPE_REQUEST_PUSH_PERMISSION_RESULT) {
            const data = message.data;
            const callback = callbackDictRef.current[data.key] || function () {};
            const enabled = data.enabled;
            const token = data.token;
            callback(enabled, token);
            if (callbackDictRef.current[data.key]) {
                delete callbackDictRef.current[data.key];
            }
        } else if (message.type === MESSAGE_TYPE_REQUEST_PUSH_PERMISSION_ANDROID_RESULT) {
            const data = message.data;
            const callback = callbackDictRef.current[data.key] || function () {};
            const enabled = data.enabled;
            const token = data.token;
            callback(enabled, token);
            if (callbackDictRef.current[data.key]) {
                delete callbackDictRef.current[data.key];
            }
        } else if (message.type === MESSAGE_TYPE_DEVICE_UID_RESULT) {
            const data = message.data;
            const callback = callbackDictRef.current[data.key] || function () {};
            const result = data.device_uid;
            callback(result);
            if (callbackDictRef.current[data.key]) {
                delete callbackDictRef.current[data.key];
            }
        } else if (message.type === MESSAGE_TYPE_USER_AGENT_RESULT) {
            const data = message.data;
            const callback = callbackDictRef.current[data.key] || function () {};
            const result = data.user_agent;
            callback(result);
            if (callbackDictRef.current[data.key]) {
                delete callbackDictRef.current[data.key];
            }
        } else if (message.type === MESSAGE_TYPE_FOREGROUND) {
            hasPushPermission((enabled) => {
                if (enabled > 0) {
                    setIsDevicePushOn(true);
                    requestPushPermission((enabled, token) => {
                        if (enabled > 0) {
                            registerPushToken(token);
                        }
                    });
                } else {
                    setIsDevicePushOn(false);
                }
            });
        }
    }

    useEffect(() => {
        if (isApp()) {
            if (isIOS()) {
                window.addEventListener('message', onRNMessage);
                messageInitializedRef.current = true;
                return () => {
                    window.removeEventListener('message', onRNMessage);
                }
            } else {
                document.addEventListener('message', onRNMessage);
                messageInitializedRef.current = true;
                return () => {
                    document.removeEventListener('message', onRNMessage);
                }
            }
        }
    }, []);

    useEffect(() => {
        if (!messageInitializedRef.current) return
        getDeviceUid(device_uid => {
           hasPushPermission(async (enabled) => {
               if (enabled > 0) {
                   setIsDevicePushOn(true);
               } else {
                   setIsDevicePushOn(false);
               }
               if (isLoading) return;
               try {
                   setIsLoading(true);
                   const res = await Axios.get('v1/push/agreement', {params: {
                       device_uid: device_uid,
                   }});
                   if (res.status < 400) {
                       setPushAgreementInfo({
                            isNightAdAgreed: Boolean(res?.data?.is_night_ad_agreed),
                            isAdAgreed: Boolean(res?.data?.is_ad_agreed),
                            isInformationAgreed: Boolean(res?.data?.is_information_agreed),
                       });
                   } else {
                       setPushAgreementInfo({
                           isNightAdAgreed: false,
                           isAdAgreed: false,
                           isInformationAgreed: false,
                       });
                   }
               } catch (e) {
                   captureException(e);
                   setPushAgreementInfo({
                       isNightAdAgreed: false,
                       isAdAgreed: false,
                       isInformationAgreed: false,
                   });
               } finally {
                   resetTogglePushAgreementCb();
                   setIsLoading(false);
               }
           });
        });
    }, [messageInitializedRef.current]);

    const contextValue = {
        isLoading,
        sendPushAgreementRequest,
        hasPushPermission,
        isDevicePushOn,
        pushAgreementInfo,
        setPushAgreementInfo,
        getDeviceUid,
        togglePushAgreementCbRef,
    };

    return (
        <>
            <PushContext.Provider value={contextValue}>
                {children}
            </PushContext.Provider>
        </>
    )
}
